<script setup lang="ts">
import type { RouteLocationRaw } from "vue-router";
import type { SpecieResource } from "@carnet/api/backoffice";

const props = defineProps<{
  specie: SpecieResource;
  // Destination du lien couvrant (détail de l'espèce), fournie par l'app hôte
  // (web : `$localePath({ name: "species.detail" })`) — absente, pas de lien.
  to?: RouteLocationRaw;
  // Override de l'image (ex : photo d'une observation) ; sinon l'image principale de l'espèce.
  imageUrl?: string;
  imageAlt?: string;
  // Voile teinté par-dessus l'image (ex : tuile cochée dans une sélection).
  imageOverlay?: boolean;
}>();

const { t } = useI18n();

const displayImageUrl = computed(() => props.imageUrl ?? props.specie.mainImage?.url);
const displayImageAlt = computed(() => props.imageAlt ?? props.specie.commonName);
</script>

<template>
  <article class="@container/tile group/tile relative flex flex-col gap-2">
    <div>
      <div class="@xs/tile:not-last:mb-3 not-last:mb-3 flex flex-col gap-x-2.5 gap-y-4 in-[.group\/sheet]:flex-row">
        <div class="in-[.group\/sheet]:max-w-20 in-[.group\/popup]:max-w-40 relative aspect-3/2 overflow-hidden bg-elevated">
          <!-- Hauteur à 111,111 % + ancrage haut : le bas (~10 %) déborde hors du cadre pour masquer la watermark. -->
          <AppImage :src="displayImageUrl" :alt="displayImageAlt" variant="species" img-class="size-full object-cover object-top" />
          <div v-if="imageOverlay" class="pointer-events-none absolute inset-0 bg-[#2C03B1]/40" aria-hidden="true" />
        </div>

        <div v-if="specie.commonName || specie.scientificName">
          <h3
            v-if="specie.commonName"
            class="text-xl @xs/tile:text-2xl font-semibold in-inverted:text-primary @xs/tile:not-last:mb-0.5"
            :class="to ? 'text-blue-700 group-hover/tile:underline' : 'text-default'"
          >
            {{ specie.commonName }}
          </h3>
          <p v-if="specie.scientificName" class="text-sm @xs/tile:text-base italic text-gray-600 in-inverted:text-primary">
            {{ specie.scientificName }}
          </p>
        </div>
      </div>
      <div v-if="specie.taxonLabel || specie.features.length" class="flex flex-col gap-y-1 not-last:mb-4 text-sm/snug text-gray-600 in-inverted:text-primary">
        <p v-if="specie.taxonLabel" class="truncate">
          {{ specie.taxonLabel }}
        </p>
        <p v-if="specie.features.length">
          {{
            specie.features
              .map((feature) => feature.label)
              .filter((e) => e)
              .join(" • ")
          }}
        </p>
      </div>
      <slot />
    </div>

    <NuxtLink
      v-if="to"
      :to
      class="absolute inset-0 rounded-[1px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary in-inverted:focus-visible:outline-white"
      :aria-label="t('species.tile.link', { name: specie.commonName })"
    />
  </article>
</template>
